/**
 * The form token of the visitor's session for the requests of Event Gallery's scripts which change
 * something (the cart, the format dialogs, the mini cart).
 *
 * The page carries no token: it can come out of a cache which holds the page of the visitor who
 * rendered it - Joomla's page cache, a cached module, the cached view of an article with a tag -
 * and Joomla renews only the hidden token fields of cached markup, not what a script reads. So
 * the token is asked for with a POST to `tokenUrl` of the cart configuration
 * (`task=session.token&format=json` on Joomla, `?eventgallery_token=1` on WordPress, which answer
 * nothing else and are never cached), once per page
 * on its first use and shared by every bundle of the page. A request the server refuses because of
 * the token - the status 403 with the code `invalid_token`, e.g. after the session expired - gets
 * a fresh token and is sent once more, never a third time
 * (decisions/request-checks-and-escaping.md).
 */

/** The code of the JSON answer to a refused token (FormTokenTrait::checkJsonToken()). */
export const INVALID_TOKEN = 'invalid_token';

/**
 * The header the server reads the token from when the cart configuration names none (`tokenHeader`):
 * the one of Joomla. WordPress names `X-WP-Nonce`, the header its REST API reads the nonce from.
 */
export const TOKEN_HEADER = 'X-CSRF-Token';

/**
 * Where the state lives: on the window, so the bundles of a page (eventgallery.js and the one of
 * the cart page) ask once between them.
 */
export const SHARED_KEY = 'EventGalleryFormToken';

interface SharedState {
    request: Promise<string> | null;
}

function shared(): SharedState {
    const w = window as any;
    if (!w[SHARED_KEY]) {
        w[SHARED_KEY] = {request: null} as SharedState;
    }
    return w[SHARED_KEY];
}

function tokenUrl(): string {
    return (window as any).EventGalleryCartConfiguration?.tokenUrl || '';
}

/** The header the token goes into: the one the cart configuration names, else TOKEN_HEADER. */
export function tokenHeader(): string {
    const header = (window as any).EventGalleryCartConfiguration?.tokenHeader;
    return typeof header === 'string' && header !== '' ? header : TOKEN_HEADER;
}

/**
 * Asks the server for the token. A request which fails is forgotten, so the next use asks again.
 */
function load(state: SharedState): Promise<string> {
    const url = tokenUrl();
    // without an address there is nothing to ask - never a POST to the page itself
    const request = (url === ''
        ? Promise.reject(new Error('The page names no address for the form token'))
        : fetch(url, {
            method: 'POST',
            credentials: 'same-origin',
            cache: 'no-store',
            headers: {'Accept': 'application/json'}
        }))
        .then((response: Response) => {
            if (!response.ok) {
                throw new Error('The form token could not be loaded: HTTP ' + response.status);
            }
            return response.json();
        })
        .then((data) => {
            // an empty token says "none": the server protects the request without one (a guest on
            // WordPress), and the request goes out without the header
            if (!data || typeof data.token !== 'string') {
                throw new Error('The answer carries no form token');
            }
            return data.token as string;
        });
    state.request = request;
    request.catch(() => {
        if (state.request === request) {
            state.request = null;
        }
    });
    return request;
}

/**
 * The token of the session: the one this page asked for already, or a new request for it.
 */
export function formToken(): Promise<string> {
    const state = shared();
    return state.request ?? load(state);
}

/**
 * A fresh token after the server refused `refused`. Requests refused together share one new
 * token: a request whose token was renewed meanwhile gets the new one without asking again.
 */
export function renewFormToken(refused: string): Promise<string> {
    const state = shared();
    const current = state.request;
    if (current === null) {
        return load(state);
    }
    return current.then(
        (token) => {
            if (token !== refused) {
                return token;
            }
            return state.request === current ? load(state) : formToken();
        },
        () => formToken()
    );
}

/**
 * Whether the answer is the refusal of the token (and nothing else that is a 403).
 */
export async function isRefusedToken(response: Response): Promise<boolean> {
    if (response.status !== 403) {
        return false;
    }
    try {
        // a copy: the caller may still read the answer
        const data = await response.clone().json();
        return data?.code === INVALID_TOKEN;
    } catch (e) {
        return false;
    }
}

/**
 * POSTs with the token in the header the cart configuration names (tokenHeader()), or without a
 * header when the token is empty; a refused token is renewed and the request sent once more.
 * The answer of the last try comes back as it is, whatever its status; a failed request for the
 * token rejects.
 *
 * @param url the task to post to
 * @param init the body and anything else of the request; the method is always POST. The body is
 *             sent twice after a refusal, so it is one which can be: URLSearchParams, FormData or
 *             a string, never a stream
 */
export async function postWithToken(url: string, init: RequestInit = {}): Promise<Response> {
    const send = (token: string) => fetch(url, {
        ...init,
        method: 'POST',
        headers: {...(init.headers as Record<string, string> || {}), ...(token === '' ? {} : {[tokenHeader()]: token})}
    });

    const token = await formToken();
    const response = await send(token);
    if (!(await isRefusedToken(response))) {
        return response;
    }
    return send(await renewFormToken(token));
}
